// src/contexts/CartContext/CartContext.js
import { createContext, useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";

export const CartContext = createContext();

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState([]);
  const [cartItemCount, setCartItemCount] = useState(0);

  const location = useLocation();

  const isFringeBenefitsPage = location?.pathname?.toLowerCase()?.includes("fringe");

  const orderKey = isFringeBenefitsPage
    ? "__extraconomy_order_fringe_benefit"
    : "__extraconomy_order";

  const updateCartFromStorage = useCallback(() => {
    const orderStr = localStorage.getItem(orderKey);

    if (orderStr) {
      const order = JSON.parse(orderStr);

      if (isFringeBenefitsPage) {
        setCartItemCount(1);
        setCart([order]);
      } else if (order.isGuestCart) {
        setCartItemCount(order?.itens?.length || 0);
        setCart(order?.itens || []);
      } else {
        setCartItemCount(order?.itens?.length || 0);
        setCart(order?.itens || []);
      }
    } else {
      setCartItemCount(0);
      setCart([]);
    }
  }, [orderKey, isFringeBenefitsPage]);

  // Inicializar carrinho
  useEffect(() => {
    updateCartFromStorage();
  }, [updateCartFromStorage]);

  // Monitorar mudanças no localStorage
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === orderKey) {
        updateCartFromStorage();
      }
    };

    // Listener para mudanças no localStorage (entre abas)
    window.addEventListener('storage', handleStorageChange);

    // Polling para mudanças no localStorage (na mesma aba)
    const interval = setInterval(() => {
      const currentOrder = localStorage.getItem(orderKey);
      const currentCartCount = isFringeBenefitsPage ? 1 : (JSON.parse(currentOrder)?.itens?.length || 0);

      if (currentCartCount !== cartItemCount) {
        updateCartFromStorage();
      }
    }, 1000); // Verificar a cada segundo

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, [orderKey, isFringeBenefitsPage, cartItemCount, updateCartFromStorage]);

  // Atualizar contagem quando o carrinho mudar
  useEffect(() => {
    setCartItemCount(cart.length);
  }, [cart]);

  const addItemToCart = useCallback(
    (item) => {
      setCart((prevCart) => [...prevCart, item]);
    },
    []
  );

  const removeItemFromCart = useCallback(
    (item) => {
      setCart((prevCart) => prevCart.filter((i) => i.product.id !== item.product.id));
    },
    []
  );

  const removeFringeBenefitItemFromCart = useCallback(
    (item) => {
      setCart([]);
    },
    []
  );

  // Função para limpar o carrinho (usada após finalizar pagamento)
  const clearCart = useCallback(() => {
    setCart([]);
    setCartItemCount(0);
  }, []);

  return (
    <CartContext.Provider
      value={{
        cart,
        cartItemCount,
        addItemToCart,
        removeItemFromCart,
        removeFringeBenefitItemFromCart,
        clearCart,
        updateCartFromStorage,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export default CartProvider;
